import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight, Salad, Target, Sparkles, Crown } from "lucide-react";
import { memberApi, setMemberToken, memberFetch, getMemberToken } from "../lib/memberApi";
import { toast } from "sonner";

const TIERS = [
  {
    id: "basic",
    name: "Foundational",
    price: 50,
    tag: "Nutrition First",
    features: [
      "AI-built 12-meal plan",
      "Macros + cooking instructions",
      "Save favorite foods + full library",
      "Weight-loss · muscle-gain · fat-loss goals",
    ],
    excluded: ["Workouts", "1-on-1 Sessions"],
    Icon: Salad,
  },
  {
    id: "accountability",
    name: "Practitioner",
    price: 100,
    tag: "Train Hard",
    highlight: true,
    features: [
      "AI-built 12-workout plan",
      "Strength · Cardio · Speed & Agility · Recovery",
      "Personalized for injuries, equipment, location",
      "1 training session/month (in-person or Zoom)",
    ],
    excluded: ["Meal Plans"],
    Icon: Target,
  },
  {
    id: "advance",
    name: "Master",
    price: 150,
    tag: "Full Stack",
    features: [
      "Everything in Practitioner",
      "Meal Plans (full nutrition stack)",
      "Progress Tracker (30-day photo check-ins)",
      "2 training sessions/month (in-person or Zoom)",
    ],
    excluded: [],
    Icon: Crown,
  },
];

function ChosenTierForm({ tier, mode, setMode, form, setForm, loading, submit }) {
  return (
    <form onSubmit={(e) => submit(e, tier.id)} className="border border-zinc-800 bg-[#0F0F0F] p-6 mt-4" data-testid={`tier-form-${tier.id}`}>
      <div className="flex border border-zinc-800 mb-4">
        <button
          type="button"
          onClick={() => setMode("register")}
          className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest ${mode === "register" ? "bg-[#E5B43A] text-black" : "text-zinc-400"}`}
        >Create Account</button>
        <button
          type="button"
          onClick={() => setMode("login")}
          className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-widest ${mode === "login" ? "bg-[#E5B43A] text-black" : "text-zinc-400"}`}
        >Sign In</button>
      </div>
      {mode === "register" && (
        <input type="text" placeholder="Full Name" required
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full bg-[#0A0A0A] border border-zinc-800 px-3 py-3 mb-2 text-white text-base focus:outline-none focus:border-[#E5B43A]"
          data-testid={`member-name-${tier.id}`} />
      )}
      <input type="email" placeholder="Email" required
        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
        className="w-full bg-[#0A0A0A] border border-zinc-800 px-3 py-3 mb-2 text-white text-base focus:outline-none focus:border-[#E5B43A]"
        data-testid={`member-email-${tier.id}`} />
      <input type="password" placeholder="Password (6+ chars)" required minLength={6}
        value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
        className="w-full bg-[#0A0A0A] border border-zinc-800 px-3 py-3 mb-4 text-white text-base focus:outline-none focus:border-[#E5B43A]"
        data-testid={`member-password-${tier.id}`} />
      <button type="submit" disabled={loading}
        data-testid={`submit-${tier.id}`}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
          tier.highlight || tier.id === "advance"
            ? "bg-[#E5B43A] text-black hover:bg-white"
            : "border border-zinc-700 text-white hover:border-[#E5B43A] hover:text-[#E5B43A]"
        } disabled:opacity-60`}>
        {loading ? "..." : (
          (tier.id === "basic" || tier.id === "accountability" || tier.id === "advance")
            ? <>Continue to Square <ArrowRight size={14} /></> :
          <>Continue <ArrowRight size={14} /></>
        )}
      </button>
      {(tier.id === "basic" || tier.id === "accountability" || tier.id === "advance") && (
        <p className="mt-3 text-[10px] tracking-widest uppercase text-zinc-500 text-center">Paid via Square · 30-day access</p>
      )}
    </form>
  );
}

export default function Membership() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e, tier) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fn = mode === "register" ? memberApi.register : memberApi.login;
      const data = await fn(form);
      setMemberToken(data.token);
      if (data.member.active) {
        navigate("/member/dashboard");
      } else if (tier === "basic" || tier === "accountability" || tier === "advance") {
        await startSquareCheckout(tier);
      } else {
        // Should never hit — all paid tiers route through Square; safety fallback
        navigate("/member/dashboard");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startSquareCheckout = async (tier) => {
    const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
    const priceMap = { basic: 50, accountability: 100, advance: 150 };
    try {
      const res = await memberFetch("/member/square-checkout", {
        method: "POST",
        body: JSON.stringify({ tier }),
      });
      window.alert(
        `✓ Account created.\n\n` +
        `You'll be sent to Square to complete payment for the ${tierLabel} tier ($${priceMap[tier]}).\n\n` +
        `AFTER PAYMENT: Davon will activate your access within 24 hours and you'll get a confirmation email. ` +
        `You can already log in to your dashboard to check status.`
      );
      window.open(res.square_url, "_blank", "noopener,noreferrer");
      navigate("/member/dashboard");
    } catch (e) {
      toast.error(e.message || "Could not start Square checkout");
    }
  };

  const [openTier, setOpenTier] = useState(null);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="membership-page">
      <header className="border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_houston-training-hub/artifacts/5f1feyf5_1781429554579.png" alt="Gain Time Fitness" className="w-8 h-8 object-cover shrink-0" />
            <span className="font-display text-lg tracking-wider">GAIN TIME · MEMBERSHIP</span>
          </Link>
          <Link to="/" className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 hover:text-[#E5B43A]">← Back</Link>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-4">// Pick Your Level</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-[0.9]">
            Lock in your<br /><span className="text-[#E5B43A]">commitment.</span>
          </h1>
          <p className="mt-6 text-zinc-300 text-base">
            Three levels. Cancel anytime. Cancel = full data wipe.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TIERS.map((t) => {
            const Icon = t.Icon;
            const isOpen = openTier === t.id;
            return (
              <div key={t.id} data-testid={`tier-card-${t.id}`}
                className={`border bg-[#141414] p-6 md:p-7 flex flex-col transition-colors ${t.highlight ? "border-[#E5B43A]" : "border-zinc-800 hover:border-zinc-600"}`}>
                {t.highlight && (
                  <span className="self-start bg-[#E5B43A] text-black text-[10px] font-bold tracking-[0.3em] uppercase px-2 py-1 mb-3">{t.tag}</span>
                )}
                {!t.highlight && (
                  <span className="self-start text-[10px] font-bold tracking-[0.3em] uppercase text-zinc-500 mb-3">{t.tag}</span>
                )}
                <Icon className="text-[#E5B43A] mb-4" size={26} />
                <h3 className="font-display text-2xl mb-1">{t.name}</h3>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="font-display text-4xl">${t.price}</span>
                  <span className="text-xs text-zinc-500">/ month</span>
                </div>
                <ul className="space-y-2 text-sm text-zinc-300 mb-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check size={14} className="text-[#E5B43A] mt-0.5 shrink-0" /><span>{f}</span></li>
                  ))}
                  {t.excluded.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-zinc-600 line-through"><span className="shrink-0 w-3.5 inline-block">—</span><span>{f}</span></li>
                  ))}
                </ul>
                <button
                  onClick={() => setOpenTier(isOpen ? null : t.id)}
                  data-testid={`pick-${t.id}`}
                  className={`mt-3 w-full px-4 py-3 text-xs font-bold uppercase tracking-widest transition-colors ${
                    isOpen ? "border border-zinc-700 text-white" : (t.highlight ? "bg-[#E5B43A] text-black hover:bg-white hover:text-black" : "border border-zinc-700 text-white hover:border-[#E5B43A] hover:text-[#E5B43A]")
                  }`}
                >
                  {isOpen ? "Cancel" : "Register"}
                </button>
                {isOpen && <ChosenTierForm tier={t} mode={mode} setMode={setMode} form={form} setForm={setForm} loading={loading} submit={submit} />}
              </div>
            );
          })}
        </div>

        <div className="mt-12 border border-zinc-800 bg-[#0F0F0F] p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5" data-testid="in-person-cta">
          <div className="min-w-0">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-2">In-Person Option</div>
            <h3 className="font-display text-2xl md:text-3xl mb-2 leading-tight">Prefer in-person training?</h3>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-xl">
              Schedule a <span className="text-white font-semibold">$25 assessment</span> in Houston.
              Cost is credited toward your training package once you sign on.
            </p>
          </div>
          <Link
            to="/#assessment"
            data-testid="in-person-cta-link"
            className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-6 py-4 text-xs hover:bg-white hover:text-[#0A0A0A] transition-colors"
          >
            Book Assessment <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-12 text-center">
          <Sparkles className="text-[#E5B43A] mx-auto mb-3" size={20} />
          <p className="text-xs tracking-widest uppercase text-zinc-500">All tiers · Cancel anytime · Paid via Square</p>
        </div>
      </main>
    </div>
  );
}
